import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  buyPrice,
  createGrid,
  findFreePlacement,
  findNestedItem,
  footprint,
  getItemDef,
  LOYALTY,
  mintPurchase,
  fittedModsDeep,
  sellPriceDeep,
  traderStanding,
  traderTier,
  TRADERS,
  usedCells,
  type BuyCategory,
  type GridState,
  type Inventory,
  type ItemInstance,
  type LoyaltyState,
  type OfferStanding,
  type PlacedItem,
  type QuestState,
  type Rotation,
  type TraderDef,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { resolveOpenBox } from "../inventory/boxLogic.js";
import {
  boxAt,
  CELL,
  centreGrab,
  instanceAt,
  viewportToUi,
  type ItemSource,
} from "../inventory/drag.js";
import { GridPanel, GRID_CELL_BACKGROUND } from "../inventory/grid.js";
import { findInspectItem, InspectDialog, type InspectedItem } from "../inventory/inspect.js";
import { ContextMenu, MenuSurface, type MenuEntry } from "../inventory/menu.js";
import { FloatingModDialog } from "../inventory/modDialog.js";
import { findOwnedWeapon, type OwnedWeapon } from "../inventory/modLogic.js";
import { ModPane } from "../inventory/modPane.js";
import { Ghost, Panel } from "../inventory/panel.js";
import { TileBody } from "../inventory/tile.js";
import { ItemTooltip } from "../inventory/tooltip.js";
import { useInventoryDrag } from "../inventory/useInventoryDrag.js";
import {
  clearDialogs,
  closeDialog,
  openDialog,
  raiseDialog,
  setModWeapon,
  useHud,
  type DialogKind,
  type OpenContainer,
} from "../store.js";
import { TEXT_DIM } from "../theme.js";
import { formatEuros } from "./format.js";
import { Badge, Bar, CardTitle, ListRow, Note, Num, Title } from "./kit.js";
import { marketEntries } from "./marketEntries.js";
import { loyaltyView, offerGateTags, offerProblem } from "./offerLogic.js";
import {
  batchLabel,
  counterView,
  markSent,
  nextSale,
  saleAnswered,
  sameStaging,
  sellButtonLabel,
  stage,
  startSellBatch,
  unstage,
  type SellBatch,
} from "./sellCounter.js";
import { TraderPortrait } from "./traderPortrait.js";

/**
 * A trader: stock on the left, the stash on the right, the sale counter
 * between. Buying is never a bare click — a click SELECTS and the Buy button
 * spends, or Ctrl+click, a drag into the stash, or the priced right-click
 * entry. The drag is this file's own (an offer has no `ItemSource`); it
 * borrows the feel and targets a whole panel (`data-buy-target`). Right-click
 * goes through `MenuSurface` with entries worded here. A gun is inspected on
 * the Werkbank: read-only for a shelf gun (`ModPane.readOnly`), the working
 * bench for an owned one.
 *
 * Sales go on a counter first (drag, Ctrl+click or menu; the same gestures
 * take them off), which lists every line and sends the lot on one button —
 * the staging that pays for the server taking a full box in one go
 * (`sellPriceDeep`). Sales go out one at a time, the next when the previous
 * is answered, since the trade budget drops anything over ten a second. See
 * `sellCounter.ts`.
 */

export interface TradersTabProps {
  euros: number;
  /**
   * The account's level for the shelf's gates. A prop, like `euros`, so a
   * render test needs no store.
   */
  level: number;
  /**
   * The quest log — the earned half of a trader's standing (`afterQuest` gates
   * and `traderRep`). Reputation is derived, never persisted.
   */
  quests: readonly QuestState[];
  /**
   * Turnover per counter — the stored half of standing. A prop for the same
   * reason as `level`; the tier arithmetic is the shared one.
   */
  loyalty: LoyaltyState;
  /**
   * Needed by the shared drag model and by `sellable` to locate an opened box.
   */
  inv: Inventory;
  stash: OpenContainer | null;
}

const OFFER_GRID_W = 8;
const SELL_ACTION = "sell";

/**
 * The price gold, borrowed for the loyalty bar: one colour that means "the
 * thing you are here for".
 */
const LOYALTY_ACCENT = "#f0d39c";

/**
 * Tier numbers as roman numerals in the header and roster (a rank reads I..IV);
 * the shelf's corner tags keep arabic — nine pixels of "III" is a smudge.
 */
const ROMAN: readonly string[] = ["", "I", "II", "III", "IV"];

/**
 * How long one staged sale waits for its `profile` before the batch moves on —
 * a dead man's handle for a socket that dies mid-batch.
 */
const SALE_ANSWER_TIMEOUT_MS = 4000;

/**
 * What each buying category is called under a portrait — lower case and vague
 * on purpose; the exact rate is on the tile.
 */
const BUY_CATEGORY_LABELS: Record<BuyCategory, string> = {
  guns: "guns",
  ammo: "ammo",
  mods: "mods",
  armor: "armour",
  meds: "meds",
  gear: "packs",
  provisions: "food & drink",
  valuables: "valuables",
  materials: "materials",
};

export function TradersTab(props: TradersTabProps): ReactElement {
  const [traderId, setTraderId] = useState<string>(TRADERS[0]?.id ?? "");
  const trader = TRADERS.find((t) => t.id === traderId) ?? TRADERS[0] ?? null;

  /**
   * What is on the counter, as stash iids. Client-only; the server hears about
   * a staged item once, when its sale is sent. See `sellCounter.ts`.
   */
  const [staged, setStaged] = useState<readonly string[]>([]);
  /** The confirmed lot, going over one sale at a time. `null` when idle. */
  const [batch, setBatch] = useState<SellBatch | null>(null);
  const selling = batch !== null;

  /**
   * What is picked off the shelf, as a def id — all an offer ever is.
   * Client-only.
   */
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  /** The shelf's own right-click menu. `dnd.menu` speaks in `ItemSource`s and
   *  has nothing to say about catalogue data, so this screen keeps a second. */
  const [offerMenu, setOfferMenu] = useState<OfferMenu | null>(null);
  /** An offer being carried towards the stash. See the header. */
  const [offerDrag, setOfferDrag] = useState<OfferDrag | null>(null);
  /**
   * A press on an offer not yet a drag — the same latch `useInventoryDrag`
   * keeps, so a click never takes capture.
   */
  const offerPress = useRef<OfferPress | null>(null);
  /** Set when a drag ends on the tile it began on, because the browser sends a
   *  `click` afterwards and that click is not a selection. */
  const swallowClick = useRef(false);

  const stashGrid = props.stash?.grid ?? null;

  // The shelf, laid out once per trader; tiles, ghost, menu and Inspect all
  // share these instances.
  const offers = useMemo(
    () => (trader === null ? createGrid(OFFER_GRID_W, 1) : offerGrid(trader)),
    [trader],
  );
  // What each staged id is worth at THIS counter, against the stash as it is
  // now. `findNestedItem` over the stash alone is exactly the server's reach.
  const view = useMemo(() => {
    return counterView(staged, (iid) => {
      if (trader === null || stashGrid === null) return null;
      const hit = findNestedItem([stashGrid], iid);
      if (hit === null) return null;
      const price = sellPriceDeep(trader, hit.item);
      if (price === null) return null;
      return { label: itemLabel(hit.item), price };
    });
  }, [staged, trader, stashGrid]);

  // Anything that stopped resolving leaves the counter, keyed by the survivors
  // so the write happens once.
  const survivorKey = view.iids.join(",");
  useEffect(() => {
    const survivors = survivorKey === "" ? [] : survivorKey.split(",");
    setStaged((prev) => (sameStaging(prev, survivors) ? prev : survivors));
  }, [survivorKey]);

  const stageItem = (iid: string): void => {
    if (selling) return;
    setStaged((prev) => stage(prev, iid));
  };
  /**
   * Ctrl+click and the menu both toggle, so undoing a staging is the gesture
   * that made it.
   */
  const toggleItem = (iid: string): void => {
    if (selling) return;
    setStaged((prev) => (prev.includes(iid) ? unstage(prev, iid) : stage(prev, iid)));
  };

  const sellAction = useMemo(
    () =>
      trader === null
        ? undefined
        : {
            id: SELL_ACTION,
            // `accepts` gets only the drag, so trader and `selling` come in
            // through the closure — a target swallowing a drag in silence is
            // worse than no.
            accepts: (from: ItemSource, item: ItemInstance): boolean =>
              !selling && sellable(trader, from, item, props.inv, props.stash),
            // A drop STAGES. Nothing is sold until the button below is pressed,
            // which is the whole point of the counter.
            drop: (_from: ItemSource, item: ItemInstance): void => {
              stageItem(item.iid);
            },
          },
    // `inv` because `accepts` reads it (a box source is sellable only while
    // the box stands in the stash); `selling` because both halves read it.
    [trader, props.inv, props.stash, selling],
  );

  // Every stash tile answers at a glance: the price in its corner (contents
  // and all), washed out when he will not touch it, a ring when staged.
  const stagedSet = useMemo(() => new Set(view.iids), [view.iids]);
  const decorateTile = useMemo(
    () =>
      trader === null
        ? undefined
        : (placed: PlacedItem): { footer?: ReactNode; dimmed?: boolean; marked?: boolean } => {
            const paid = sellPriceDeep(trader, placed.item);
            if (paid === null) return { dimmed: true };
            return {
              footer: <span className="metaoffer-price">{formatEuros(paid)}</span>,
              marked: stagedSet.has(placed.item.iid),
            };
          },
    [trader, stagedSet],
  );

  const dnd = useInventoryDrag(props.inv, props.stash, {
    action: sellAction,
    onQuickMove: (from, item) => {
      if (trader !== null && sellable(trader, from, item, props.inv, props.stash)) {
        toggleItem(item.iid);
      }
    },
  });
  const { clear } = dnd;
  /** The shelf's transient state, all of it, in one call — the counterpart of
   *  the drag hook's `clear`. */
  const clearOffer = useCallback((): void => {
    offerPress.current = null;
    swallowClick.current = false;
    setSelectedOffer(null);
    setOfferMenu(null);
    setOfferDrag(null);
  }, []);
  // Leaving the counter packs the bench, the dialogs and the selection away —
  // the bench could be standing on a gun that only exists on this shelf.
  useEffect(() => {
    return () => {
      clear();
      clearOffer();
      setModWeapon(null);
      clearDialogs();
    };
  }, [clear, clearOffer]);

  // Escape abandons the shelf's gesture, capture phase on `window` ahead of
  // the game's listener, so it cancels the gesture rather than the menu.
  const offerLive = offerMenu !== null || offerDrag !== null;
  useEffect(() => {
    if (!offerLive) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.code !== "Escape") return;
      ev.preventDefault();
      ev.stopPropagation();
      offerPress.current = null;
      setOfferMenu(null);
      setOfferDrag(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [offerLive]);

  // Space presses Sell, only while the button would do something. Capture
  // phase, and a press on a focused button is swallowed before it becomes a
  // click.
  const canSell = !selling && view.iids.length > 0;
  const sellIidsKey = view.iids.join(",");
  useEffect(() => {
    if (!canSell) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.code !== "Space" || ev.repeat) return;
      const target = ev.target as HTMLElement | null;
      if (target !== null && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      ev.preventDefault();
      ev.stopPropagation();
      const started = startSellBatch(sellIidsKey.split(","));
      if (started !== null) setBatch(started);
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [canSell, sellIidsKey]);

  // Same contract as the gear tab: a double-click records an iid and each
  // window lives as long as its subject resolves. An Inspect ref is asked of
  // the player's own reach FIRST and of the shelf only when that is empty.
  const dialogs = useHud((s) => s.dialogs);
  // And of the shelf's guns' PARTS last — not for sale on their own, so no
  // asking price.
  const resolvedDialogs = dialogs.map((ref) => {
    const owned = ref.t === "inspect" ? findInspectItem(props.inv, ref.iid, props.stash) : null;
    const shelf = ref.t === "inspect" && owned === null ? offerInspect(offers, ref.iid) : null;
    const shelfPart =
      ref.t === "inspect" && owned === null && shelf === null
        ? offerPartInspect(offers, ref.iid)
        : null;
    return {
      ref,
      box: ref.t === "box" ? resolveOpenBox(props.inv, props.stash, ref.iid) : null,
      inspected: owned ?? shelf ?? shelfPart,
      onShelf: shelf !== null || shelfPart !== null,
      asking: shelf !== null,
    };
  });
  // Keyed by a string so the effect fires only when the set of casualties
  // changes, not on every render.
  const goneKey = resolvedDialogs
    .filter((d) => d.box === null && d.inspected === null)
    .map((d) => `${d.ref.t}:${d.ref.iid}`)
    .join(",");
  useEffect(() => {
    if (goneKey === "") return;
    for (const key of goneKey.split(",")) {
      // First colon only: the kind is ours, the iid is the server's.
      const idx = key.indexOf(":");
      closeDialog(key.slice(0, idx) as DialogKind, key.slice(idx + 1));
    }
  }, [goneKey]);

  // The read-only bench's subject, resolved in the same order: own reach
  // first, shelf second. A shelf gun is dressed as an `OwnedWeapon` with no
  // area.
  const modWeaponIid = useHud((s) => s.modWeaponIid);
  const ownedWeapon =
    modWeaponIid === null ? null : findOwnedWeapon(props.inv, modWeaponIid, props.stash);
  const shelfWeapon =
    modWeaponIid === null || ownedWeapon !== null ? null : offerWeapon(offers, modWeaponIid);
  const modWeapon = ownedWeapon ?? shelfWeapon;
  const modGone = modWeaponIid !== null && modWeapon === null;
  useEffect(() => {
    // A stale cleanup must not close a newer selection.
    if (modGone && useHud.getState().modWeaponIid === modWeaponIid) setModWeapon(null);
  }, [modGone, modWeaponIid]);

  // --- The batch: two effects and a timer enforcing one rule — never more
  // than one `sell` in flight. SEND whenever nobody is out; ANSWER on every
  // `profile` (one follows every sale, accepted or refused).
  const profile = useHud((s) => s.profile);
  useEffect(() => {
    if (batch === null || trader === null) return;
    const iid = nextSale(batch);
    if (iid === null) return;
    uiActions().sell(trader.id, iid);
    setBatch(markSent(batch, iid));
  }, [batch, trader]);
  useEffect(() => {
    setBatch((b) => (b === null || b.inFlight === null ? b : saleAnswered(b)));
  }, [profile]);
  // ...and the dead man's handle, in case no profile ever comes.
  const inFlight = batch?.inFlight ?? null;
  useEffect(() => {
    if (inFlight === null) return;
    const timer = window.setTimeout(() => {
      setBatch((b) => (b === null || b.inFlight === null ? b : saleAnswered(b)));
    }, SALE_ANSWER_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [inFlight]);

  if (trader === null) return <Note>No traders in the valley.</Note>;

  /**
   * What the player brings to THIS counter, once, by the shared function (the
   * same call the server makes). Not a `useMemo` — it sits after a bail.
   */
  const standing = traderStanding(props.loyalty, props.quests, props.level, trader.id);
  /** The header's numbers — rep, rung, and how far the next one is. */
  const loyalty = loyaltyView(props.loyalty, props.quests, trader.id);

  const carried =
    dnd.drag === null ? null : instanceAt(props.inv, props.stash, dnd.drag.from, dnd.drag.iid);
  const overCounter = dnd.drag?.hover?.t === "action" && dnd.drag.hover.action === SELL_ACTION;
  const canSellCarried =
    !selling &&
    carried !== null &&
    dnd.drag !== null &&
    sellable(trader, dnd.drag.from, carried, props.inv, props.stash);
  // One price, computed once; the hint prints it.
  const offer = canSellCarried && carried !== null ? sellPriceDeep(trader, carried) : null;

  // The bench's price line: the ASKING price on the shelf, what this counter
  // pays for one in the stash, no line for a gun he will not buy.
  const benchPrice =
    modWeapon === null
      ? null
      : shelfWeapon !== null
        ? buyPrice(trader, modWeapon.def.id)
        : sellPriceDeep(trader, modWeapon.item);
  const benchHeader =
    benchPrice === null ? undefined : (
      <span style={{ fontSize: 11, color: TEXT_DIM }}>
        {shelfWeapon !== null ? formatEuros(benchPrice) : `Sells for ${formatEuros(benchPrice)}`}
      </span>
    );

  /**
   * The counter's entry on a stash tile's menu, priced in the label. Absent for
   * anything the counter would not take; a toggle for a staged one.
   */
  const sellEntries = (item: ItemInstance, from: ItemSource): readonly MenuEntry[] => {
    if (selling) return [];
    if (stagedSet.has(item.iid)) {
      return [
        {
          label: "Take off counter",
          run: () => {
            setStaged((prev) => unstage(prev, item.iid));
          },
        },
      ];
    }
    if (!sellable(trader, from, item, props.inv, props.stash)) return [];
    const paid = sellPriceDeep(trader, item);
    if (paid === null) return [];
    return [
      {
        label: `Sell · ${formatEuros(paid)}`,
        run: () => {
          stageItem(item.iid);
        },
      },
    ];
  };

  /**
   * Switching counters empties the one you stood at and the shelf's selection —
   * both belong to the previous trader.
   */
  const selectTrader = (id: string): void => {
    if (id === traderId) return;
    setTraderId(id);
    setStaged([]);
    clearOffer();
  };

  // --- The shelf. `offerProblem` is re-asked at the moment the money would
  // move: the stash fills up under the player's hands while a menu is open.

  const buyOffer = (defId: string): void => {
    if (offerProblem(trader, defId, props.euros, stashGrid, standing) !== null) return;
    uiActions().buy(trader.id, defId);
  };

  /**
   * Inspect goes through the shelf's own instance. A gun goes to the read-only
   * bench.
   */
  const inspectOffer = (defId: string): void => {
    const placed = offers.items.find((p) => p.item.defId === defId);
    if (placed === undefined) return;
    const def = getItemDef(placed.item.defId);
    if (def !== null && def.kind === "weapon") setModWeapon(placed.item.iid);
    else openDialog("inspect", placed.item.iid);
  };

  const offerHandlers: OfferHandlers = {
    select: (defId) => {
      // The click after a drag that began and ended on the same tile is not a
      // selection.
      if (swallowClick.current) {
        swallowClick.current = false;
        return;
      }
      setSelectedOffer(defId);
    },

    inspect: (defId) => {
      offerPress.current = null;
      setOfferDrag(null);
      setOfferMenu(null);
      inspectOffer(defId);
    },

    menu: (ev, defId) => {
      ev.preventDefault();
      ev.stopPropagation();
      offerPress.current = null;
      setOfferDrag(null);
      // Only one menu on screen at a time, whichever half of it opened one.
      dnd.closeMenu();
      setOfferMenu({ px: ev.clientX, py: ev.clientY, about: { t: "offer", defId } });
    },

    down: (ev, placed) => {
      if (ev.button !== 0) return;
      const def = getItemDef(placed.item.defId);
      if (def === null) return;
      ev.preventDefault();
      // Kept off the inventory root, whose press handler dismisses the menu.
      ev.stopPropagation();
      dnd.closeMenu();
      setOfferMenu(null);
      swallowClick.current = false;

      // Ctrl+click BUYS — the mirror of the Ctrl+click that stages a sale.
      if (ev.ctrlKey) {
        offerPress.current = null;
        buyOffer(def.id);
        return;
      }

      // The offer is carried by its centre, wherever on the tile it was
      // pressed — see `centreGrab`.
      const grab = centreGrab(footprint(def, placed.rot));
      offerPress.current = {
        pointerId: ev.pointerId,
        startX: ev.clientX,
        startY: ev.clientY,
        defId: def.id,
        rot: placed.rot,
        grabX: grab.x,
        grabY: grab.y,
      };
    },

    move: (ev) => {
      const press = offerPress.current;
      if (press !== null && press.pointerId === ev.pointerId) {
        // No button held means the press is stale (the release happened
        // elsewhere before capture); clear the latch.
        if (ev.buttons === 0) {
          offerPress.current = null;
          return;
        }
        const travelled = Math.hypot(ev.clientX - press.startX, ev.clientY - press.startY);
        if (travelled < OFFER_DRAG_THRESHOLD_PX) return;
        offerPress.current = null;
        ev.stopPropagation();
        // Capture goes to the TILE (which outlives the drag), not the root
        // (whose capture belongs to `useInventoryDrag`).
        ev.currentTarget.setPointerCapture(ev.pointerId);
        setOfferDrag({
          pointerId: ev.pointerId,
          defId: press.defId,
          rot: press.rot,
          px: ev.clientX,
          py: ev.clientY,
          grabX: press.grabX,
          grabY: press.grabY,
          over: overBuyTarget(ev.clientX, ev.clientY),
        });
        return;
      }
      const carrying = offerDrag;
      if (carrying === null || carrying.pointerId !== ev.pointerId) return;
      ev.stopPropagation();
      setOfferDrag({
        ...carrying,
        px: ev.clientX,
        py: ev.clientY,
        over: overBuyTarget(ev.clientX, ev.clientY),
      });
    },

    up: (ev) => {
      if (offerPress.current?.pointerId === ev.pointerId) offerPress.current = null;
      const carrying = offerDrag;
      if (carrying === null || carrying.pointerId !== ev.pointerId) return;
      ev.stopPropagation();
      setOfferDrag(null);
      swallowClick.current = true;
      // Re-asked at the release point: a pointerup can carry a position no move
      // reported.
      if (overBuyTarget(ev.clientX, ev.clientY)) buyOffer(carrying.defId);
    },

    cancel: (ev) => {
      if (offerPress.current?.pointerId === ev.pointerId) offerPress.current = null;
      setOfferDrag((d) => (d === null || d.pointerId !== ev.pointerId ? d : null));
    },
  };

  /**
   * The read-only bench's menu on a fitted part: look at it, and go looking for
   * it.
   */
  const partEntries = (iid: string): readonly MenuEntry[] => {
    const entries: MenuEntry[] = [
      {
        label: "Inspect",
        run: () => {
          openDialog("inspect", iid);
        },
      },
    ];
    const part = offerPartInspect(offers, iid);
    if (part !== null) entries.push(...marketEntries(part.def.id));
    return entries;
  };

  /**
   * The shelf's right-click menu. Inspect always; Buy absent rather than
   * disabled when it cannot be made (the tile is already washed out).
   */
  const offerEntries = (defId: string): readonly MenuEntry[] => {
    const entries: MenuEntry[] = [
      {
        label: "Inspect",
        run: () => {
          inspectOffer(defId);
        },
      },
      // Before Buy: questions about the purchase rather than the purchase
      // itself.
      ...marketEntries(defId),
    ];
    if (offerProblem(trader, defId, props.euros, stashGrid, standing) === null) {
      entries.push({
        label: `Buy · ${formatEuros(buyPrice(trader, defId))}`,
        run: () => {
          buyOffer(defId);
        },
      });
    }
    return entries;
  };

  // The Buy button reads the selection and names its price — the same one on
  // the tile.
  const selectedDef = selectedOffer === null ? null : getItemDef(selectedOffer);
  const selectedProblem =
    selectedOffer === null
      ? null
      : offerProblem(trader, selectedOffer, props.euros, stashGrid, standing);

  // Green for a buy that would go through, red for one that would not, a quiet
  // outline while the drag is elsewhere.
  const draggedProblem =
    offerDrag === null
      ? null
      : offerProblem(trader, offerDrag.defId, props.euros, stashGrid, standing);
  const draggedOffer =
    offerDrag === null
      ? null
      : offers.items.find((p) => p.item.defId === offerDrag.defId) ?? null;
  const buyTargetRing =
    offerDrag === null
      ? undefined
      : !offerDrag.over
        ? "inset 0 0 0 1px rgba(214, 205, 194, 0.34)"
        : draggedProblem === null
          ? "inset 0 0 0 2px rgba(140, 220, 148, 0.9)"
          : "inset 0 0 0 2px rgba(232, 104, 92, 0.9)";

  // A press anywhere but a shelf tile drops the shelf's pending press.
  const root = {
    ...dnd.root,
    onPointerDown: (): void => {
      offerPress.current = null;
      dnd.root.onPointerDown();
    },
  };

  return (
    <div
      {...root}
      style={{
        display: "flex",
        gap: 14,
        alignItems: "stretch",
        width: "100%",
        maxWidth: 1200,
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        // The floating dialogs centre themselves on the nearest positioned
        // ancestor, which has to be this screen and not the whole menu.
        position: "relative",
        overflowX: "auto",
        touchAction: "none",
      }}
    >
      <aside
        className="mm-card mm-card-tight"
        style={{
          flex: "0 0 252px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <CardTitle>TRADERS</CardTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {TRADERS.map((entry) => (
            <ListRow
              key={entry.id}
              selected={entry.id === trader.id}
              // Switching mid-sale would finish the batch at somebody else's
              // prices.
              disabled={selling}
              onClick={() => {
                selectTrader(entry.id);
              }}
              leading={
                <span className="mm-portrait" style={{ display: "flex" }}>
                  <TraderPortrait trader={entry} size={38} style={{ borderRadius: "inherit" }} />
                </span>
              }
              title={entry.name}
              subtitle={`Buys ${buyLine(entry)}`}
              // WHERE YOU STAND WITH EACH OF THEM, on the roster rather than
              // only on the counter you happen to be at. Standing does not
              // travel — being Trusted by the gamekeeper buys nothing at the
              // inn — so the interesting sentence is which of the five knows
              // you, and that sentence cannot be read one trader at a time.
              trailing={
                <Badge tone="neutral">
                  Tier {ROMAN[traderTier(props.loyalty, props.quests, entry.id)] ?? ""}
                </Badge>
              }
            />
          ))}
        </div>

        {/* THE COUNTER. Always here, always the drop target, and never a
            surprise: what is on it is written out line by line, and the only
            thing that sends any of it is the button at the bottom. */}
        <div
          data-inventory-action={SELL_ACTION}
          className="metacounter"
          data-active={overCounter ? "true" : "false"}
          data-allowed={canSellCarried ? "true" : "false"}
        >
          <div className="metacounter-head">
            <span>COUNTER</span>
            <span style={{ color: view.rows.length === 0 ? TEXT_DIM : "#f0d39c" }}>
              {formatEuros(view.total)}
            </span>
          </div>

          {/* What the counter has to say, in the one place it says it: how to
              put something on it while it is empty, and what the thing in the
              player's hand would add while a drag is in flight. Both are the
              same line, so a counter with ten things on it still answers the
              question a drag is asking. */}
          {carried !== null ? (
            <div className="metacounter-hint">
              {offer === null ? `${trader.name} won't buy this` : `Add for ${formatEuros(offer)}`}
            </div>
          ) : view.rows.length === 0 ? (
            <div className="metacounter-hint">
              Drag, Ctrl + click, or right-click stash items to put them here; the same again
              takes them off. Nothing is sold until you press the button.
            </div>
          ) : null}

          {view.rows.length === 0 ? null : (
            <ul className="metacounter-list">
              {view.rows.map((row) => (
                <li key={row.iid}>
                  <button
                    type="button"
                    className="metacounter-row"
                    disabled={selling}
                    title="Take this back off the counter"
                    onClick={() => {
                      setStaged((prev) => unstage(prev, row.iid));
                    }}
                  >
                    <span className="metacounter-name">{row.label}</span>
                    <span className="metacounter-price">{formatEuros(row.price)}</span>
                    <span aria-hidden="true" className="metacounter-x">
                      ×
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="metadeploy metacounter-sell"
            disabled={selling || view.rows.length === 0}
            onClick={() => {
              const started = startSellBatch(view.iids);
              if (started !== null) setBatch(started);
            }}
          >
            {batch === null ? sellButtonLabel(view) : batchLabel(batch)}
            {canSell ? <kbd>Space</kbd> : null}
          </button>
        </div>
      </aside>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          minHeight: 0,
          // As wide as the shelf's panel (grid + its padding and edge), so a
          // long blurb wraps instead of pushing the stash off the screen.
          flex: `0 0 ${OFFER_GRID_W * CELL + 34}px`,
          minWidth: 0,
        }}
      >
        {/* WHO YOU ARE DEALING WITH, over his shelf: portrait, a sentence, and
            where you stand. Standing is a fact about a PERSON, not his stock,
            and the tier tags on the tiles mean nothing until the ladder has
            been read once — so it sits under the name. The bar is the only
            thing here that is not a number: "74 rep" says nothing without a
            scale, a quarter-filled bar says "a while yet". */}
        <section
          className="mm-card"
          data-trader-tier={loyalty.tier}
          style={{ gap: 10, padding: "14px 16px" }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span className="mm-portrait" style={{ display: "flex" }}>
              <TraderPortrait trader={trader} size={56} style={{ borderRadius: "inherit" }} />
            </span>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
              <Title size="lg">{trader.name}</Title>
              <Note>{trader.blurb}</Note>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ color: LOYALTY_ACCENT, fontSize: 12, fontWeight: 500 }}>
                Tier {ROMAN[loyalty.tier] ?? ""} · {loyalty.name}
              </span>
              <Num tone="dim">{loyalty.rep} rep</Num>
            </div>
            <Bar thin fill01={loyalty.fraction} />
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <Note>
                {loyalty.nextTier === null || loyalty.repToNext === null
                  ? `${loyalty.name} — the best shelf in the valley`
                  : `${loyalty.repToNext} rep to Tier ${ROMAN[loyalty.nextTier] ?? ""}`}
              </Note>
              {/* Where the number comes from; without it the bar moves for
                  reasons nobody can name. */}
              <Note style={{ fontSize: 11, textAlign: "right" }}>
                Finished quests, and €{LOYALTY.eurPerRep} traded = 1 rep.
              </Note>
            </div>
          </div>
        </section>

      <Panel
        title="For sale"
        header={
          <button
            type="button"
            className="metabtn"
            data-buy-button=""
            disabled={selectedOffer === null || selectedProblem !== null}
            title={
              selectedProblem ??
              (selectedDef === null ? "Pick something off the shelf first" : `Buy ${selectedDef.name}`)
            }
            onClick={() => {
              if (selectedOffer !== null) buyOffer(selectedOffer);
            }}
          >
            {selectedDef === null
              ? "Buy"
              : `Buy ${selectedDef.name} · ${formatEuros(buyPrice(trader, selectedDef.id))}`}
          </button>
        }
      >
        <OfferGrid
          grid={offers}
          trader={trader}
          euros={props.euros}
          standing={standing}
          stash={stashGrid}
          selected={selectedOffer}
          handlers={offerHandlers}
        />
        {/* The shortcuts, written down. The button above is the discoverable
            way and needs no explaining; these three are the ones a player would
            otherwise have to be told about by somebody else. */}
        <div className="metacounter-hint" style={{ maxWidth: OFFER_GRID_W * CELL }}>
          Ctrl + click or drag an item into the stash to buy it. Right-click to inspect.
        </div>
      </Panel>
      </div>

      <Panel
        title="Stash"
        header={
          props.stash === null ? null : (
            <span style={{ fontSize: 11, color: TEXT_DIM }}>
              {usedCells(props.stash.grid)}/{props.stash.grid.w * props.stash.grid.h}
            </span>
          )
        }
      >
        {/* THE BUY TARGET. A whole region rather than a cell, because a
            purchase does not land where it was dropped: the server's own first
            fit decides that, and a ghost snapping to a cell it will not end up
            in would be a promise this screen cannot keep. */}
        <div
          data-buy-target=""
          style={{
            padding: 4,
            borderRadius: 4,
            boxShadow: buyTargetRing,
            transition: "box-shadow 90ms ease",
          }}
        >
          {props.stash === null ? (
            <Note>Loading stash…</Note>
          ) : (
            <>
              {props.stash.grid.items.length === 0 ? (
                <Note style={{ display: "block", marginBottom: 8 }}>
                  The stash is empty. Purchases appear here.
                </Note>
              ) : null}
              <GridPanel
                title=""
                area="container"
                grid={props.stash.grid}
                drag={dnd.drag}
                decorateTile={decorateTile}
                {...dnd.tiles}
              />
            </>
          )}
        </div>
      </Panel>

      {/* THE WERKBANK, on the shelf's stock and the stash's guns alike — and
          READ-ONLY only for the first. A gun still in the trader's case shows
          what it is made of and what it would do, and nothing that would change
          it: the parts list that says what could be screwed onto it is left
          out, because the thing is not the player's to work on. A gun the
          player owns gets the gear tab's live bench, drag handlers and all, so
          a scope can come off a rifle right here before the rifle goes on the
          counter. Everything behind it stays live either way, so the counter
          can still be worked while the window is up. */}
      {modWeapon === null ? null : (
        <FloatingModDialog
          title={`WEAPONS › ${modWeapon.def.name.toUpperCase()}`}
          rootRef={dnd.root.ref}
          header={benchHeader}
          onClose={() => {
            setModWeapon(null);
          }}
        >
          {(contentScale) =>
            shelfWeapon !== null ? (
              <ModPane
                inv={props.inv}
                container={props.stash}
                weapon={modWeapon}
                containerLabel="Stash"
                containerOwned
                readOnly
                previewScale={contentScale}
                onPartContextMenu={(ev, iid) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  dnd.closeMenu();
                  setOfferMenu({ px: ev.clientX, py: ev.clientY, about: { t: "part", iid } });
                }}
              />
            ) : (
              <ModPane
                inv={props.inv}
                container={props.stash}
                weapon={modWeapon}
                containerLabel="Stash"
                containerOwned
                drag={dnd.drag}
                previewScale={contentScale}
                onTilePointerDown={dnd.tiles.onTilePointerDown}
                onTileContextMenu={dnd.tiles.onTileContextMenu}
              />
            )
          }
        </FloatingModDialog>
      )}

      {/* The dialogs. An opened bag/box floats over the counter exactly as it
          does over the gear screen, and a box STANDING IN THE STASH is sold out
          of exactly as the stash floor is — so its tiles carry the same corner
          prices and the same washed-out refusals. The condition is `sellable`'s,
          put to a panel rather than a drag: only a stash-hosted box gets prices,
          because a price on a thing the counter will not take would be a lie —
          and a worn bag's hoisted grid is exactly such a thing, whether or not
          this screen has any way to open one.

          Inspect on something the player OWNS carries the trader, which is what
          adds the "Sells for" row — the same number the hover tooltip prints
          here and nowhere else. Inspect on something still on the SHELF carries
          the asking price instead, and no trader at all: see the dialog below.
          NO GUN EVER REACHES EITHER: a weapon's inspect screen is the bench
          above, and both of those prices are on its handle instead.

          AFTER THE BENCH, and that is the point of the placement (the gear tab
          does the same): the two are sibling windows at the same z-index, so a
          box opened while the bench is up lands ON TOP of it rather than under
          it — and the bench is movable for the times it is the thing in the way.

          ONE LIST, the two kinds interleaved, because the ORDER IS THE STACKING
          (see `dialogs`): splitting them into two blocks would pin every
          description above every box forever, which at a counter is precisely
          backwards — comparing two things you are deciding between is the whole
          reason both windows are up. */}
      {resolvedDialogs.map(({ ref, box, inspected, onShelf, asking }, i) => {
        const key = `${ref.t}:${ref.iid}`;
        const nudge = i * 26;
        const raise = () => raiseDialog(ref.t, ref.iid);
        if (ref.t === "box") {
          return box === null ? null : (
            <Panel
              overlay
              key={key}
              nudge={nudge}
              onRaise={raise}
              title={box.def.name}
              header={
                <span style={{ fontSize: 11, color: TEXT_DIM }}>
                  {box.fill.used}/{box.fill.total}
                </span>
              }
              onClose={() => {
                closeDialog("box", ref.iid);
              }}
            >
              <GridPanel
                title=""
                {...box.target}
                grid={box.grid}
                drag={dnd.drag}
                decorateTile={
                  box.target.area === "inside" &&
                  boxAt(props.inv, props.stash, box.item.iid)?.area === "container"
                    ? decorateTile
                    : undefined
                }
                {...dnd.tiles}
              />
            </Panel>
          );
        }
        return inspected === null ? null : (
          <InspectDialog
            key={key}
            nudge={nudge}
            onRaise={raise}
            subject={inspected}
            // A thing still on the shelf gets the asking price and no trader.
            trader={onShelf ? null : trader}
            buyPrice={asking ? buyPrice(trader, inspected.def.id) : null}
            onClose={() => {
              closeDialog("inspect", ref.iid);
            }}
          />
        );
      })}

      <Ghost drag={dnd.drag} inv={props.inv} container={props.stash} />
      {offerDrag === null || draggedOffer === null ? null : (
        <OfferGhost drag={offerDrag} placed={draggedOffer} />
      )}
      {dnd.tip === null ? null : (
        <ItemTooltip tip={dnd.tip} inv={props.inv} container={props.stash} trader={trader} />
      )}
      {dnd.menu === null ? null : (
        <ContextMenu
          menu={dnd.menu}
          inv={props.inv}
          container={props.stash}
          place="menu"
          // The one DISCOVERABLE gesture: the menu says "Sell · €123" in so
          // many words.
          extraEntries={sellEntries}
          onClose={dnd.closeMenu}
        />
      )}
      {offerMenu === null ? null : (
        <MenuSurface
          px={offerMenu.px}
          py={offerMenu.py}
          entries={
            offerMenu.about.t === "offer"
              ? offerEntries(offerMenu.about.defId)
              : partEntries(offerMenu.about.iid)
          }
          onClose={() => {
            setOfferMenu(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Whether this trader would take this instance out of the stash: it must be
 * coming out of the stash (a box standing in it counts, via `boxAt`'s
 * top-level area), and he must have a DEEP price for it. Cash needs no clause
 * — `euro` has no buying category.
 */
function sellable(
  trader: TraderDef,
  from: ItemSource,
  item: ItemInstance,
  inv: Inventory,
  stash: OpenContainer | null,
): boolean {
  if (sellPriceDeep(trader, item) === null) return false;
  if (from.t === "grid") return from.area === "container";
  return from.t === "box" && boxAt(inv, stash, from.boxIid)?.area === "container";
}

/**
 * One counter line: the name and its stack. An unknown def falls back to its
 * id.
 */
function itemLabel(item: ItemInstance): string {
  const def = getItemDef(item.defId);
  const name = def?.name ?? item.defId;
  return item.stack > 1 ? `${name} ×${item.stack}` : name;
}

/** "guns, ammo, mods" — the categories this trader takes, in `buys` order. */
function buyLine(trader: TraderDef): string {
  return (Object.keys(trader.buys) as BuyCategory[])
    .map((category) => BUY_CATEGORY_LABELS[category])
    .join(", ");
}

// ---------------------------------------------------------------------------

/** How far a press must travel before it is a drag rather than a click — the
 *  same four pixels every other tile in the game is picked up with. */
const OFFER_DRAG_THRESHOLD_PX = 4;

/**
 * Where the shelf's menu was asked for: an offer, or a part fitted to a shelf
 * gun. One state, so only one menu stands.
 */
interface OfferMenu {
  /** Viewport pixels. */
  px: number;
  py: number;
  about: { t: "offer"; defId: string } | { t: "part"; iid: string };
}

/** A press on an offer that may yet become a drag. */
interface OfferPress {
  pointerId: number;
  startX: number;
  startY: number;
  defId: string;
  rot: Rotation;
  /** Where inside the tile it was grabbed, so the ghost hangs off the cursor
   *  at the point the player actually took hold of. */
  grabX: number;
  grabY: number;
}

/**
 * An offer in the air. `over` is re-asked on every move and decides the
 * release.
 */
interface OfferDrag {
  pointerId: number;
  defId: string;
  rot: Rotation;
  px: number;
  py: number;
  grabX: number;
  grabY: number;
  over: boolean;
}

/** What a tile does with the pointer, all of it owned by `TradersTab` — the
 *  grid below is markup and nothing else. */
interface OfferHandlers {
  select: (defId: string) => void;
  inspect: (defId: string) => void;
  menu: (ev: MouseEvent<HTMLButtonElement>, defId: string) => void;
  down: (ev: PointerEvent<HTMLButtonElement>, placed: PlacedItem) => void;
  move: (ev: PointerEvent<HTMLButtonElement>) => void;
  up: (ev: PointerEvent<HTMLButtonElement>) => void;
  cancel: (ev: PointerEvent<HTMLButtonElement>) => void;
}

/**
 * Whether a viewport point is inside something willing to be bought into — read
 * off the DOM like the inventory's drop zones.
 */
function overBuyTarget(px: number, py: number): boolean {
  if (typeof document === "undefined") return false;
  const hit = document.elementFromPoint(px, py);
  return hit !== null && hit.closest("[data-buy-target]") !== null;
}

/** The offer `iid` names, for an Inspect dialog opened off the shelf. */
function offerInspect(offers: GridState, iid: string): InspectedItem | null {
  const placed = offers.items.find((p) => p.item.iid === iid);
  if (placed === undefined) return null;
  const def = getItemDef(placed.item.defId);
  return def === null ? null : { item: placed.item, def };
}

/**
 * The PART `iid` names inside a shelf gun, for an Inspect off the read-only
 * bench. Walks every weapon offer's tree at the bench's depth.
 */
function offerPartInspect(offers: GridState, iid: string): InspectedItem | null {
  for (const placed of offers.items) {
    const def = getItemDef(placed.item.defId);
    if (def === null || def.kind !== "weapon") continue;
    const hit = fittedModsDeep(def, placed.item).find((mod) => mod.item.iid === iid);
    if (hit !== undefined) return { item: hit.item, def: hit.def };
  }
  return null;
}

/**
 * The GUN `iid` names on the shelf, for a read-only bench. `area` is `null` —
 * the shelf's stock is in no grid.
 */
function offerWeapon(offers: GridState, iid: string): OwnedWeapon | null {
  const placed = offers.items.find((p) => p.item.iid === iid);
  if (placed === undefined) return null;
  const def = getItemDef(placed.item.defId);
  return def === null || def.kind !== "weapon" ? null : { item: placed.item, def, area: null };
}

function OfferGrid(props: {
  grid: GridState;
  trader: TraderDef;
  euros: number;
  standing: OfferStanding;
  stash: GridState | null;
  selected: string | null;
  handlers: OfferHandlers;
}): ReactElement {
  const { grid } = props;

  return (
    <div
      style={{
        display: "inline-block",
        border: "1px solid rgba(214, 205, 194, 0.22)",
        borderRadius: 3,
        background: "rgba(0, 0, 0, 0.34)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: grid.w * CELL,
          height: grid.h * CELL,
          backgroundImage: GRID_CELL_BACKGROUND,
          backgroundSize: `${CELL}px ${CELL}px`,
          overflow: "hidden",
        }}
      >
        {grid.items.map((placed) => (
          <OfferTile
            key={placed.item.iid}
            placed={placed}
            trader={props.trader}
            euros={props.euros}
            standing={props.standing}
            stash={props.stash}
            selected={placed.item.defId === props.selected}
            handlers={props.handlers}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One thing on the shelf. A `<button>` (Enter, Space, focus ring) but NEVER
 * `disabled`: a disabled button eats the right-click and double-click, and
 * "I cannot afford this, what is it?" is a question a shop must answer. The
 * refusal is drawn instead (`dimmed`, named in the title).
 */
function OfferTile(props: {
  placed: PlacedItem;
  trader: TraderDef;
  euros: number;
  standing: OfferStanding;
  stash: GridState | null;
  selected: boolean;
  handlers: OfferHandlers;
}): ReactElement | null {
  const def = getItemDef(props.placed.item.defId);
  if (def === null) return null;

  const price = buyPrice(props.trader, def.id);
  const problem = offerProblem(props.trader, def.id, props.euros, props.stash, props.standing);
  const size = footprint(def, props.placed.rot);
  // The gates as facts about the shelf line: a tag stays on after its gate is
  // reached. Only the padlocks are about the player.
  const gates = offerGateTags(props.trader, def.id, props.standing);
  const locked = gates.some((gate) => gate.shut);
  // The refusal spoken when a shut gate is the answer. Lower-cased, since it
  // reads as a clause after a separator.
  const spoken = !locked || problem === null ? "" : ` · ${lowerFirst(problem)}`;

  return (
    <button
      type="button"
      className="metaoffer"
      data-trader-offer={def.id}
      aria-selected={props.selected}
      aria-label={`${def.name} · ${formatEuros(price)}${spoken}`}
      title={`${def.name} · ${formatEuros(price)} · ${
        problem ?? "Ctrl+click or drag to the stash to buy"
      }`}
      // A plain click SELECTS; Ctrl+click has already bought by now (see
      // `down`) and must not also move the selection under the button.
      onClick={(ev) => {
        if (!ev.ctrlKey) props.handlers.select(def.id);
      }}
      onDoubleClick={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        props.handlers.inspect(def.id);
      }}
      onContextMenu={(ev) => {
        props.handlers.menu(ev, def.id);
      }}
      onPointerDown={(ev) => {
        props.handlers.down(ev, props.placed);
      }}
      onPointerMove={props.handlers.move}
      onPointerUp={props.handlers.up}
      onPointerCancel={props.handlers.cancel}
      style={{
        position: "absolute",
        left: props.placed.x * CELL,
        top: props.placed.y * CELL,
        width: size.w * CELL,
        height: size.h * CELL,
        // The tile takes the pointer for the whole gesture; nothing here is
        // scrolled or panned by it.
        touchAction: "none",
      }}
    >
      <TileBody
        def={def}
        item={props.placed.item}
        cursor="grab"
        dimmed={problem !== null}
        marked={props.selected}
        footer={<span className="metaoffer-price">{formatEuros(price)}</span>}
      />
      {/* The gates, in the one corner the price does not use, stacked because a
          line may carry three of them. Absolutely positioned over the tile body
          rather than handed to it: every other tile in the game shares that
          component, and none of the rest has a shelf rule to draw.

          `aria-hidden`, all of them: the same facts are already in the label
          above, and a screen reader that read "Lvl 5" and "Quest" out of the
          corner would say the gate twice and the quest's name never. The
          `title` on the quest tag is for the mouse, which has no other way of
          finding out WHICH errand — the word "Quest" is all that fits. */}
      {gates.length === 0 ? null : (
        <span className="metaoffer-gates" aria-hidden="true">
          {gates.map((gate) => (
            <span
              key={gate.text}
              className={gate.shut ? "metaoffer-gate metaoffer-gate-shut" : "metaoffer-gate"}
              title={gate.title}
            >
              {gate.shut ? <LockGlyph /> : null}
              {gate.text}
            </span>
          ))}
        </span>
      )}
    </button>
  );
}

/** A refusal turned into a clause. One character, but it is the difference
 *  between a screen reader saying a sentence and saying a heading. */
function lowerFirst(text: string): string {
  return text.length === 0 ? text : `${text[0]?.toLowerCase() ?? ""}${text.slice(1)}`;
}

/**
 * A padlock at label size, drawn rather than typed: the only padlock a font
 * gives is a colour emoji that renders differently everywhere. `currentColor`.
 * Exported for the market tab so the two padlocks are one path.
 */
export function LockGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 8 10" width="7" height="9" aria-hidden="true" focusable="false">
      <path
        d="M2.1 4.4V2.7a1.9 1.9 0 0 1 3.8 0v1.7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <rect x="0.6" y="4.3" width="6.8" height="5.2" rx="1" fill="currentColor" />
    </svg>
  );
}

/**
 * The offer under the cursor while carried. `Ghost`'s twin — an offer is in no
 * inventory, so `instanceAt` finds nothing. `pointer-events: none` is
 * load-bearing for `overBuyTarget`.
 */
function OfferGhost(props: { drag: OfferDrag; placed: PlacedItem }): ReactElement | null {
  const def = getItemDef(props.placed.item.defId);
  if (def === null) return null;
  const f = footprint(def, props.drag.rot);
  const pointer = viewportToUi(props.drag.px, props.drag.py);

  return (
    <div
      style={{
        position: "fixed",
        left: pointer.x - props.drag.grabX,
        top: pointer.y - props.drag.grabY,
        width: f.w * CELL,
        height: f.h * CELL,
        pointerEvents: "none",
        opacity: 0.88,
        filter: "drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6))",
        zIndex: 40,
      }}
    >
      <TileBody def={def} item={props.placed.item} rot={props.drag.rot} />
    </div>
  );
}

/** Pack catalogue entries with the same first-fit rules as every owned grid.
 * The height grows only as needed, keeping each trader's shelf compact. */
function offerGrid(trader: TraderDef): GridState {
  let grid = createGrid(OFFER_GRID_W, 1);

  for (const offer of trader.offers) {
    const item = mintPurchase(offer.defId, () => `offer:${trader.id}:${offer.defId}`);
    const def = getItemDef(offer.defId);
    if (item === null || def === null) continue;

    let at = findFreePlacement(grid, def);
    while (at === null) {
      grid = { ...grid, h: grid.h + 1 };
      at = findFreePlacement(grid, def);
    }
    grid.items.push({ item, x: at.x, y: at.y, rot: at.rot });
  }

  return grid;
}
